import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import {
  apiFetchPartner,
  apiPartner2FASetup,
  apiPartner2FAConfirm,
  apiPartner2FADisable,
} from "../../api/index.js";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog";
import {
  colors,
  font,
  text,
  card,
  cardBody,
  inp,
  button,
  pill,
} from "./partnerTheme";

const serverMessage = (err, fallback) =>
  err?.response?.data?.error?.message || fallback;

/* The authenticator apps this is verified against all emit six digits, and the
   server verifies with a one step window, so anything longer is a typo rather
   than a code worth sending. */
const CODE_LENGTH = 6;
const onlyDigits = (value) => (value || "").replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);

const PartnerSecurity = () => {
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const operationMode = user?.result?.operationMode;

  /*
   * The three 2FA routes look the signed in id up in the ThreePLPartner
   * collection, so they only ever act on the partner OWNER's login. A staff
   * member who used them would either get a 404 or, worse, read the controls
   * as protecting their own account when their own account is untouched. The
   * honest screen for staff is therefore no controls at all.
   */
  const isStaffAccount = operationMode === "3PL_STAFF";

  const confirmDialog = useConfirmDialog();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  /* "idle" shows the state, "confirm" holds the QR and the code field,
     "codes" shows the backup codes the server returns exactly once. */
  const [step, setStep] = useState("idle");
  const [qrCode, setQrCode] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (isStaffAccount) {
      setLoading(false);
      return;
    }
    if (!partnerId) {
      setLoading(false);
      setError("No partner account is attached to this login, so there is nothing to secure here.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await apiFetchPartner(partnerId);
      const partner = res?.data?.data?.partner;
      if (res?.data?.success && partner) {
        setTwoFAEnabled(!!partner.twoFA?.enabled);
      } else {
        setError("Your account security settings could not be loaded.");
      }
    } catch (err) {
      setError(serverMessage(err, "Your account security settings could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [partnerId, isStaffAccount]);

  useEffect(() => {
    load();
  }, [load]);

  const startSetup = async () => {
    try {
      setBusy(true);
      const res = await apiPartner2FASetup();
      const payload = res?.data?.data;
      if (res?.data?.success && payload?.qrCode) {
        setQrCode(payload.qrCode);
        setManualSecret(payload.secret || "");
        setCode("");
        setStep("confirm");
      } else {
        toast.error("Two factor setup could not be started.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "Two factor setup could not be started."));
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (code.length !== CODE_LENGTH) {
      toast.error("Enter the 6 digit code shown in your authenticator app.");
      return;
    }
    try {
      setBusy(true);
      const res = await apiPartner2FAConfirm(code);
      if (res?.data?.success) {
        setTwoFAEnabled(true);
        /* The server hashes nothing here and never returns these again, so the
           only chance the owner has to keep them is this render. */
        setBackupCodes(res.data.data?.backupCodes || []);
        setQrCode("");
        setManualSecret("");
        setCode("");
        setStep("codes");
        toast.success(res.data.message || "Two factor authentication is on.");
      } else {
        toast.error("The code could not be confirmed.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The code could not be confirmed."));
    } finally {
      setBusy(false);
    }
  };

  const cancelSetup = () => {
    setStep("idle");
    setQrCode("");
    setManualSecret("");
    setCode("");
  };

  const runDisable = async (currentCode) => {
    try {
      setBusy(true);
      const res = await apiPartner2FADisable(currentCode);
      if (res?.data?.success) {
        setTwoFAEnabled(false);
        setBackupCodes([]);
        setCode("");
        setStep("idle");
        toast.success(res.data.message || "Two factor authentication is off.");
      } else {
        toast.error("Two factor authentication was not turned off.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "Two factor authentication was not turned off."));
    } finally {
      setBusy(false);
    }
  };

  const askDisable = () => {
    if (code.length !== CODE_LENGTH) {
      toast.error("Enter your current 6 digit code to turn two factor off.");
      return;
    }
    const currentCode = code;
    confirmDialog.ask({
      title: "Turn off two factor authentication?",
      body:
        "After this, your password is the only thing protecting this account. Anyone who learns it can sign in as you and act on your riders, orders and payouts.",
      note: "Your saved backup codes stop working as well.",
      tone: "danger",
      confirmLabel: "Turn it off",
      busyLabel: "Turning off",
      onConfirm: () => runDisable(currentCode),
    });
  };

  if (isStaffAccount) {
    return (
      <div className="content" style={{ fontFamily: font.sans, background: colors.ground }}>
        <div style={{ marginBottom: 20 }}>
          <h4 style={text.title}>Account security</h4>
        </div>
        <div style={card}>
          <div style={cardBody}>
            <p style={{ ...text.body, margin: 0 }}>
              Sign in security for this company is managed by the partner account owner.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, textAlign: "center", color: colors.muted }}>
          <p style={{ ...text.body, margin: 0 }}>Loading account security...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={{ fontFamily: font.sans, background: colors.ground }}>
      <div style={{ marginBottom: 20 }}>
        <h4 style={text.title}>Account security</h4>
        <p style={text.subtitle}>
          Two factor authentication for the partner account that owns your riders,
          orders and payouts.
        </p>
      </div>

      {error ? (
        <div style={{ ...card, ...cardBody, background: colors.redSoft, borderColor: colors.red }}>
          <p style={{ ...text.body, color: colors.red, margin: 0 }}>{error}</p>
        </div>
      ) : null}

      <div style={card}>
        <div style={cardBody}>
          <p style={text.section}>Two factor authentication</p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 14,
              marginBottom: 14,
            }}
          >
            <span style={pill(twoFAEnabled ? "good" : "warn")}>
              {twoFAEnabled ? "On" : "Off"}
            </span>
            <span style={{ ...text.body, fontWeight: 600, color: colors.ink }}>
              {twoFAEnabled
                ? "Sign in needs your password and a code from your authenticator app."
                : "Sign in needs your password only."}
            </span>
          </div>

          {step === "idle" && !twoFAEnabled ? (
            <>
              <p style={{ ...text.body, margin: "0 0 18px", maxWidth: 720 }}>
                Turning this on adds a second step at sign in. You scan a code once with
                an authenticator app on your phone, and from then on the app gives you a
                fresh 6 digit code to type after your password.
              </p>
              <button
                type="button"
                style={button("primary", {
                  opacity: busy ? 0.6 : 1,
                  cursor: busy ? "not-allowed" : "pointer",
                })}
                disabled={busy}
                onClick={startSetup}
              >
                {busy ? "Working..." : "Turn on two factor"}
              </button>
            </>
          ) : null}

          {step === "confirm" ? (
            <>
              <p style={{ ...text.body, margin: "0 0 16px", maxWidth: 720 }}>
                Scan this with your authenticator app, then type the 6 digit code it
                shows to finish. Two factor is not on until you confirm.
              </p>

              {qrCode ? (
                <img
                  src={qrCode}
                  alt="Two factor setup code"
                  style={{
                    width: 200,
                    height: 200,
                    display: "block",
                    marginBottom: 16,
                    border: `1px solid ${colors.line}`,
                    borderRadius: 12,
                    background: colors.surface,
                    padding: 8,
                  }}
                />
              ) : null}

              {manualSecret ? (
                <div style={{ marginBottom: 18 }}>
                  <label style={text.label}>Or enter this key by hand</label>
                  <div
                    style={{
                      fontFamily: font.mono,
                      fontSize: 13,
                      color: colors.ink,
                      background: colors.lineSoft,
                      border: `1px solid ${colors.line}`,
                      borderRadius: 8,
                      padding: "10px 12px",
                      wordBreak: "break-all",
                      maxWidth: 420,
                    }}
                  >
                    {manualSecret}
                  </div>
                </div>
              ) : null}

              <div style={{ maxWidth: 220, marginBottom: 16 }}>
                <label style={text.label}>Code from your app</label>
                <input
                  {...inp({ fontFamily: font.mono, letterSpacing: "0.3em" })}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(onlyDigits(e.target.value))}
                />
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  style={button("primary", {
                    opacity: busy ? 0.6 : 1,
                    cursor: busy ? "not-allowed" : "pointer",
                  })}
                  disabled={busy}
                  onClick={confirmSetup}
                >
                  {busy ? "Confirming..." : "Confirm"}
                </button>
                <button
                  type="button"
                  style={button("secondary")}
                  disabled={busy}
                  onClick={cancelSetup}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : null}

          {step === "codes" ? (
            <>
              <p style={{ ...text.body, margin: "0 0 14px", maxWidth: 720 }}>
                Two factor is on. Save these backup codes somewhere safe. Each one works
                once, and they are the only way back in if you lose the phone. They are
                shown here and nowhere else.
              </p>
              <div
                style={{
                  fontFamily: font.mono,
                  fontSize: 13,
                  color: colors.ink,
                  background: colors.lineSoft,
                  border: `1px solid ${colors.line}`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 18,
                  maxWidth: 420,
                }}
              >
                {backupCodes.map((backupCode) => (
                  <div key={backupCode} style={{ padding: "3px 0" }}>
                    {backupCode}
                  </div>
                ))}
              </div>
              <button type="button" style={button("secondary")} onClick={() => setStep("idle")}>
                I have saved them
              </button>
            </>
          ) : null}

          {step === "idle" && twoFAEnabled ? (
            <>
              <p style={{ ...text.body, margin: "0 0 16px", maxWidth: 720 }}>
                To turn two factor off, type the code your authenticator app is showing
                right now. This proves the phone is still yours.
              </p>
              <div style={{ maxWidth: 220, marginBottom: 16 }}>
                <label style={text.label}>Current code</label>
                <input
                  {...inp({ fontFamily: font.mono, letterSpacing: "0.3em" })}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(onlyDigits(e.target.value))}
                />
              </div>
              <button
                type="button"
                style={button("danger", {
                  opacity: busy ? 0.6 : 1,
                  cursor: busy ? "not-allowed" : "pointer",
                })}
                disabled={busy}
                onClick={askDisable}
              >
                {busy ? "Working..." : "Turn off two factor"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <ConfirmDialog {...confirmDialog.props} />
    </div>
  );
};

export default PartnerSecurity;
